import { Component, OnInit, ViewChild } from '@angular/core';
import { Action, NavigationDataKey, NavigationType, Referer, UserPermissions } from '@shared-libs/enums';
import { NavigationService } from '@shared-services/navigation.service';
import { IClient, IClientHistory } from '@internal-models/client.model';
import { IFormGroup } from '@shared-libs/interfaces';
import { ClientFormFactory } from '@internal-factories/forms/client-form.factory';
import { formIsValid, getSegmentName, translateSegments } from '@shared-libs/helpers';
import { ClientProvider } from '@internal-providers/client.provider';
import { Segment } from '@beego/ngx-segment';
import { IDataQuery, ITableData, TableDesktopComponent } from '@beego/ngx-table';
import { LoanComponent } from '@internal-components/device-components/loan-component/loan.component';
import { MessageService } from '@shared-services/message.service';
import { ReservationComponent } from '@internal-components/event-components/reservation-component/reservation.component';
import { ClientHistoryTableFactory } from '@internal-factories/tables/client-history-table.factory';
import { BeegoAppointmentDetailsComponent } from '@internal-components/beego-appointment-components/beego-appointment-details-component/beego-appointment-details.component';
import { BeegoAppointmentProvider } from '@internal-providers/beego-appointment.provider';
import { ValidationService } from '@shared-services/validation.service';
import { UserManager } from '@shared-managers/user.manager';
import { remove } from 'lodash';
import { ReservationProvider } from '@internal-providers/reservation.provider';
import { isAfter } from 'date-fns';
import { LoanProvider } from '@internal-providers/loan.provider';
@Component({
	selector: 'client-desktop-page',
	templateUrl: 'client.desktop.page.html',
	styleUrls: ['./client.desktop.page.scss'],
})
export class ClientDesktopPage implements OnInit {
	@ViewChild(TableDesktopComponent) private readonly tableComponent: TableDesktopComponent;
	public readonly tableHeaders = ClientHistoryTableFactory.getHeaders();
	public readonly segments: Array<Segment> = translateSegments(['CLIENT_DATA', 'CLIENT_HISTORY']);
	public selectedSegment: string = getSegmentName(this.segments[0]);
	public getSegmentName = getSegmentName;

	public client: IClient;
	public clientForm: IFormGroup<Partial<IClient>>;
	public history: Array<IClientHistory>;

	public readonly UserPermissions: typeof UserPermissions = UserPermissions;

	constructor(
		private readonly navigationService: NavigationService,
		private readonly provider: ClientProvider,
		private readonly messageService: MessageService,
		private readonly beegoAppointmentProvider: BeegoAppointmentProvider,
		private readonly validationService: ValidationService,
		private readonly userManager: UserManager,
		private readonly reservationProvider: ReservationProvider,
		private readonly loanProvider: LoanProvider
	) {
		if (this.userManager.hasNotAllPermissions([UserPermissions.ReadClientsHistory])) {
			remove(this.segments, (segment) => segment === getSegmentName(this.segments[1]));
		}
	}

	public readonly dataRequest = (query: IDataQuery): Promise<ITableData<IClientHistory>> =>
		this.provider.getHistory(this.client.id, query);

	public onSelectedSegmentChange(segment: string): void {
		this.selectedSegment = segment;
	}

	public dataChanged(history: Array<IClientHistory>): void {
		this.history = history;
	}

	public async ngOnInit(): Promise<void> {
		this.client = this.navigationService.getNavigationData(NavigationDataKey.client);
		this.client = await this.provider.getOne(this.client.id);

		if (this.navigationService.getReferer() == Referer.ReservationComponent) {
			this.clientForm = ClientFormFactory.createForReservation(this.client);
		} else {
			this.clientForm = ClientFormFactory.createForClientsPage(this.client);
		}
	}

	public async updateClient(): Promise<void> {
		if (formIsValid(this.clientForm, ClientDesktopPage)) {
			const clientForm = ClientFormFactory.getValue(this.clientForm);
			const client = await this.provider.update(clientForm.id, clientForm);
			this.navigationService.navigateBack({ action: Action.UPDATE, client });
		}
	}

	public async deleteClient(): Promise<void> {
		if (await this.validationService.actionIsConfirmed('CLIENT')) {
			await this.provider.delete(this.client.id);
			this.navigationService.navigateBack({ action: Action.DELETE });
		}
	}

	public async openLoanOrReservation(history: IClientHistory): Promise<void> {
		let openPromise: Promise<{ action: Action }>;

		if (history.Loan) {
			const loan = await this.loanProvider.getOne(history.Loan.id);
			openPromise = this.navigationService.navigateTo<{ action: Action }>(NavigationType.modal, LoanComponent, {
				devices: loan.Devices,
				loan: loan,
			});
		}

		if (history.Reservation) {
			const reservation = await this.reservationProvider.getOne(history.Reservation.id);
			openPromise = this.navigationService.navigateTo(NavigationType.modal, ReservationComponent, {
				action: isAfter(new Date(), new Date(reservation.CalendarEvent.endsAt)) ? Action.SELECT : Action.UPDATE,
				reservation: reservation,
				calendarEvent: { ...reservation.CalendarEvent, Event: reservation.Event },
				eventAttendanceType: reservation.Event.attendanceType,
			});
		}

		if (history.BeegoAppointment) {
			const beegoAppointment = await this.beegoAppointmentProvider.getOne(history.BeegoAppointment.AppointmentId);
			openPromise = this.navigationService.navigateTo(NavigationType.modal, BeegoAppointmentDetailsComponent, {
				beegoAppointment,
			});
		}

		void openPromise
			?.then((result) => {
				if (result?.action) {
					this.tableComponent.refreshData();
					this.messageService.showSavedMessage();
				}
			})
			.catch();
	}
}
